// B1: client-side mirror of the backend classify_indexability_cause so the
// issue detail view can surface the actionable root cause (and whether a redirect
// is appropriate) even before a fix suggestion is generated. Keep in sync with
// backend/api/services/broken_link_resolver.py::classify_indexability_cause.

export type IndexabilityCause =
	| "dead_page"
	| "noindex"
	| "canonicalised"
	| "other";

export function classifyIndexabilityCause(
	statusCode: number | null | undefined,
	indexabilityStatus: string | null | undefined,
): IndexabilityCause {
	if (statusCode !== null && statusCode !== undefined) {
		const code = Number(statusCode);
		if (!Number.isNaN(code) && code >= 400) return "dead_page";
	}

	const label = (indexabilityStatus ?? "").trim().toLowerCase();
	if (!label) return "other";
	if (
		label.includes("client error") ||
		label.includes("server error") ||
		label.includes("not found")
	) {
		return "dead_page";
	}
	if (label.includes("noindex") || label.includes("no index")) return "noindex";
	if (label.includes("canonical")) return "canonicalised";
	return "other";
}

export interface IndexabilityCauseMeta {
	label: string;
	explanation: string;
	/** Whether a 301 redirect is an appropriate auto-fix (backend gates on this). */
	redirectEligible: boolean;
}

export const INDEXABILITY_CAUSE_META: Record<
	IndexabilityCause,
	IndexabilityCauseMeta
> = {
	dead_page: {
		label: "Dead page (4xx/5xx)",
		explanation:
			"The page returns an error status, so it can be safely redirected to the nearest live page.",
		redirectEligible: true,
	},
	noindex: {
		label: "Intentional noindex",
		explanation:
			"The page works (HTTP 200) but is tagged 'noindex' — usually deliberate (thank-you, login, or QA pages). Review only; it will not be auto-redirected.",
		redirectEligible: false,
	},
	canonicalised: {
		label: "Canonical mismatch",
		explanation:
			"The page canonicalises to another URL. Confirm the canonical target is correct before changing anything. Review only.",
		redirectEligible: false,
	},
	other: {
		label: "Other / unknown",
		explanation:
			"Marked non-indexable for a reason we don't auto-fix. Review manually before acting.",
		redirectEligible: false,
	},
};
